/**
 * Row-level quick actions shared by the NiV leads list and clinics list:
 *
 *  - CopyPhoneButton — one click copies the phone (local 05x-xxxxxxx form).
 *  - AiSummaryPeek   — "AI magic" sparkle; hover/focus opens a portal popover
 *                      with the stored AI chat summary; click copies it.
 *  - NotePeek        — notepad glyph; hover/focus opens the same popover with
 *                      the record's free-text note (freeNotes); click copies.
 *
 * All stop propagation so the parent row's onClick (open page) does not fire.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Copy, Sparkle, Check, Notepad } from "@phosphor-icons/react";
import { useToast } from "../../../../contexts/ToastContext";
import { formatPhoneIL } from "../../../../utils/phoneUtils";
import "./RowQuickActions.css";

const GAP = 8;
const COPIED_MS = 1400;

const copyText = async (text) => {
  await navigator.clipboard.writeText(text);
};

const formatSavedAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "short" });
};

export function CopyPhoneButton({ phone, className = "" }) {
  const { showError } = useToast();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  if (!phone) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await copyText(formatPhoneIL(phone));
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), COPIED_MS);
    } catch (error) {
      console.error("[CK-7314] Error copying phone:", {
        errorCode: "CK-7314",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("לא ניתן להעתיק");
    }
  };

  return (
    <button
      type="button"
      className={`nivqa-copy${copied ? " is-copied" : ""} ${className}`}
      onClick={handleClick}
      onKeyDown={(e) => e.stopPropagation()}
      title={copied ? "הועתק" : "העתק טלפון"}
      aria-label="העתק טלפון"
    >
      {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
    </button>
  );
}

/**
 * Generic hover/focus peek: small trigger glyph + portal popover with text.
 * `tone` picks the color family ("ai" violet | "note" amber) — tokens live in
 * RowQuickActions.css under .nivqa-peek-<tone>.
 */
function PeekPopover({ text, tone, size, title, meta, Icon, copiedToast, errorCode }) {
  const { showSuccess, showError } = useToast();
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: "bottom" });

  const updatePosition = useCallback(() => {
    const a = anchorRef.current?.getBoundingClientRect();
    const p = popRef.current?.getBoundingClientRect();
    if (!a || !p) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let placement = "bottom";
    let top = a.bottom + GAP;
    if (top + p.height > vh - 8 && a.top - p.height - GAP > 8) {
      placement = "top";
      top = a.top - p.height - GAP;
    }
    // RTL: align popover's right edge to the anchor's right edge when possible
    let left = a.right - p.width;
    left = Math.max(8, Math.min(left, vw - p.width - 8));
    setPos({ top, left, placement });
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    const onScroll = () => setVisible(false);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible, updatePosition]);

  useEffect(() => () => hideTimerRef.current && clearTimeout(hideTimerRef.current), []);

  if (!text) return null;

  const show = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setVisible(true);
  };
  const hideSoon = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setVisible(false), 160);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await copyText(text);
      showSuccess(copiedToast);
    } catch (error) {
      console.error(`[${errorCode}] Error copying ${tone} text from row:`, {
        errorCode,
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("לא ניתן להעתיק");
    }
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`nivqa-peek nivqa-peek-${tone} nivqa-peek-${size}`}
        onClick={handleCopy}
        onKeyDown={(e) => e.stopPropagation()}
        onMouseEnter={show}
        onMouseLeave={hideSoon}
        onFocus={show}
        onBlur={hideSoon}
        title={`${title} — לחיצה מעתיקה`}
        aria-label={title}
        aria-expanded={visible}
      >
        <Icon size={size === "sm" ? 11 : 14} weight="fill" />
      </button>
      {visible &&
        createPortal(
          <div
            ref={popRef}
            className={`nivqa-pop nivqa-pop-${tone} nivqa-pop--${pos.placement}`}
            style={{ top: pos.top, left: pos.left }}
            role="tooltip"
            dir="rtl"
            onMouseEnter={show}
            onMouseLeave={hideSoon}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nivqa-pop-head">
              <span className="nivqa-pop-title">
                <Icon size={12} weight="fill" />
                {title}
              </span>
              {meta && <span className="nivqa-pop-meta">{meta}</span>}
              <button type="button" className="nivqa-pop-copy" onClick={handleCopy}>
                <Copy size={12} />
                העתק
              </button>
            </div>
            <pre className="nivqa-pop-text">{text}</pre>
          </div>,
          document.body
        )}
    </>
  );
}

export function AiSummaryPeek({ aiSummary, size = "sm" }) {
  const text = aiSummary?.text || "";
  if (!text) return null;
  return (
    <PeekPopover
      text={text}
      tone="ai"
      size={size}
      title="סיכום AI"
      meta={aiSummary.messagesCount != null ? `${aiSummary.messagesCount} הודעות` : null}
      Icon={Sparkle}
      copiedToast="הסיכום הועתק"
      errorCode="CK-7315"
    />
  );
}

/** Free-text note peek (lead `freeNotes` / clinic CRM `freeNotes`). */
export function NotePeek({ text, updatedAt, size = "sm" }) {
  const clean = (text || "").trim();
  if (!clean) return null;
  const saved = formatSavedAt(updatedAt);
  return (
    <PeekPopover
      text={clean}
      tone="note"
      size={size}
      title="הערה"
      meta={saved ? `עודכן ${saved}` : null}
      Icon={Notepad}
      copiedToast="ההערה הועתקה"
      errorCode="CK-7316"
    />
  );
}
